import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { api, setApi401Callback } from '../services/api';

interface User {
  id: number;
  email: string;
  name: string | null;
  role: 'super_admin' | 'admin' | 'editor';
  is_super_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check if a JWT token is expired by decoding its payload
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Add 60s buffer so we don't use a token that's about to expire
    return payload.exp * 1000 < Date.now() + 60000;
  } catch {
    return true;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');

    if (storedToken && storedUser) {
      // Check if token is expired before restoring session
      if (isTokenExpired(storedToken)) {
        // Token expired — clean up silently, user will see login page
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('user_email');
        localStorage.removeItem('user_name');
        delete api.defaults.headers.common['Authorization'];
      } else {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));

        // Set API authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

        // Also set in localStorage for components that need direct access
        localStorage.setItem('user_email', JSON.parse(storedUser).email);
        localStorage.setItem('user_name', JSON.parse(storedUser).name || '');
      }
    }

    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;

      // Store in state
      setToken(access_token);
      setUser(userData);

      // Store in localStorage
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('user_email', userData.email);
      localStorage.setItem('user_name', userData.name || '');

      // Set API authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    } catch (error: any) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        throw new Error('Account temporarily locked due to too many failed attempts. Please wait 15 minutes or contact your administrator.');
      }
      throw new Error(detail || 'Login failed');
    }
  };

  const logout = useCallback(() => {
    // Clear state
    setUser(null);
    setToken(null);

    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');

    // Clear API authorization header
    delete api.defaults.headers.common['Authorization'];
  }, []);

  // Register/unregister the 401 callback for the API interceptor
  useEffect(() => {
    setApi401Callback(logout);
    return () => setApi401Callback(null);
  }, [logout]);

  const refreshUser = async () => {
    if (!token) return;

    try {
      const response = await api.get('/auth/me');
      const userData = response.data;

      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('user_email', userData.email);
      localStorage.setItem('user_name', userData.name || '');
    } catch (error) {
      // Token might be expired
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin' || !!user?.is_super_admin,
    login,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Higher-order component for protected routes
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="spinner"></span>
          <span className="text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Higher-order component for super-admin-only routes
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { isSuperAdmin, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="spinner"></span>
          <span className="text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500">You need super admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Higher-order component for admin-only routes
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAdmin, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="spinner"></span>
          <span className="text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
